/**
 * Job Post Validation Schema (Zod)
 * 
 * Validation schemas for all job post steps
 * Compatible with existing JobDescriptionEntity structure
 */

import { z } from 'zod';
import { DEFAULT_PASS_PERCENTAGE, MIN_PASS_PERCENTAGE, MAX_PASS_PERCENTAGE } from '../utils/constants';
import { Requirements } from '../../../Entities/JobDescription';
import { InterviewRoundType } from '../../../Dto/GenerateJobDescription.dto';
import { AdditionalRequirementDto } from '../../../Dto/RequirementCategory.dto';

// Step 0: AI Generator
const step0Schema = z.object({
  prompt: z.string().optional(),
  appJobId: z.string().optional(), // ATS job post ID for syncing
  appType: z.string().optional(), // ATS app type (e.g., 'lever', 'zoho')
  skills: z.array(z.string()).optional(), // Skills extracted from job description by AI
});

// Step 1: Basic Details (matches JobDescriptionEntity structure)
const step1Schema = z.object({
  title: z.string()
    .min(1, 'Job title is required'),
  
  position: z.string()
    .min(1, 'Position is required'),
  
  numberOfOpenings: z.number()
    .min(1, 'Number of openings must be at least 1')
    .max(1000, 'Number of openings cannot exceed 1000'),
  
  noticePeriod: z.string().default('0'),
  
  // CTC: Number only (no currency appended)
  ctc: z.number().nullable().optional(),
  
  // Currency: Optional separate field
  currency: z.string().optional(),
  
  currencyCode: z.string().default('INR'),
  
  endDate: z.string().nullable().optional(),

  description: z.string()
    .min(1, 'Description is required'),

  notifications: z.object({
    email: z.boolean().default(true),
    whatsapp: z.boolean().default(true),
  }).default({ email: true, whatsapp: true }),
});

// Step 2: Requirements (matches existing Requirements interface)
const requirementSchema = z.object({
  id: z.number(),
  topic: z.string().default(''), // Always empty string per FR-009
  description: z.string().min(1, 'Requirement description is required'),
  mustMatch: z.boolean(),
});

// Requirements History Entry Schema
export const requirementsHistoryEntrySchema = z.object({
  id: z.string(),
  timestamp: z.number(),
  prompt: z.string(),
  requirementsBefore: z.array(requirementSchema),
  requirementsAfter: z.array(requirementSchema),
  action: z.string().refine((val) => val === 'refine' || val === 'generate', {
    message: 'Action must be either "refine" or "generate"',
  }),
  summary: z.string().optional(), // AI-generated summary of changes
});

export type RequirementsHistoryEntry = z.infer<typeof requirementsHistoryEntrySchema>;

const step2Schema = z.object({
  requirements: z.array(requirementSchema)
    .min(1, 'At least one requirement is required'),
  
  additionalRequirements: z.array(z.any()).optional(), // AdditionalRequirementDto[]
  
  history: z.array(requirementsHistoryEntrySchema).optional(), // Refinement history
}).refine(
  (data) => data.requirements.some((req) => req.mustMatch === true),
  {
    message: 'At least one requirement must be marked as "Must Match"',
    path: ['requirements'],
  }
);

// Step 3: Interview Rounds (extended with display metadata)
const interviewRoundSchema = z.object({
  // API fields (sent to backend)
  interviewRoundId: z.string().nullable(),
  interviewRoundNumber: z.number(),
  isMustQualify: z.boolean(),
  passPercentage: z.number().min(MIN_PASS_PERCENTAGE).max(MAX_PASS_PERCENTAGE).optional(), // Per-round pass percentage
  isAIGenerated: z.boolean().optional(), // Whether questions are AI-generated

  // Display metadata (NOT sent to backend, stored in form for UI)
  // Optional because it may not be available on initial load
  _displayMetadata: z.object({
    title: z.string().optional(),
    description: z.string().optional(),
    questionGenerationPrompt: z.string().optional(),
    evaluationPrompt: z.string().optional(),
    answerType: z.string().optional(),
    _id: z.string().optional(),
    // Version tracking for cache invalidation
    _version: z.number().optional(), // Increment when round is updated
    _fetchedAt: z.number().optional(), // Timestamp when metadata was fetched
  }).optional(),
});

const step3Schema = z.object({
  interviewRounds: z.array(interviewRoundSchema),
  isProfileMatchRequired: z.boolean().default(true),
  interviewRoundExpiry: z.number().min(1).default(5), // Common expiry in days for all rounds
  // passPercentage: z.number().min(0).max(100).optional(), // Commented out for this release, to be considered in next release
});

// Step 4: Job Alignment (Manual Screening Questions)
const manualScreeningQuestionSchema = z.object({
  id: z.any(), // Can be string or number
  description: z.string().min(1, 'Screening question is required'),
  order: z.number(),
});

const step4Schema = z.object({
  jobAlignment: z.object({
    isAIGenerated: z.boolean().default(true),
    passPercentage: z.number().min(MIN_PASS_PERCENTAGE).max(MAX_PASS_PERCENTAGE).default(DEFAULT_PASS_PERCENTAGE), // Pass percentage
    isMustQualify: z.boolean().default(true),
  }).optional(),
  manualScreeningQuestions: z.array(manualScreeningQuestionSchema).optional(),
}).refine((data) => {
  // Rule 1: If jobAlignment is deleted/undefined, no validation needed (both AI and manual questions are optional)
  if (!data.jobAlignment) {
    return true; // No Job Alignment means no questions needed
  }

  // Rule 2: If jobAlignment exists and isAIGenerated is true, manual questions are optional
  if (data.jobAlignment.isAIGenerated === true) {
    return true; // AI questions will be generated, manual questions are optional
  }

  // Rule 3: If jobAlignment exists and isAIGenerated is false, manual questions are optional
  // (Skip & Proceed allows proceeding without manual questions)
  return true;
}, {
  message: 'At least one manual screening question is required when AI-generated questions are disabled',
  path: ['manualScreeningQuestions'], // Error will be shown on manualScreeningQuestions field
});

// Step 5: Post Assessment (matches JobDescriptionEntity)
const interviewerSchema = z.object({
  _id: z.string(),
  firstName: z.string(),
  lastName: z.string(),
  email: z.string(), // Required in Interviewer interface
  referenceType: z.string(), // Will be validated/transformed to "User" in transformers
  role: z.string(),
});

const step5Schema = z.object({
  onlineInterview: z.boolean().default(false),
  interviewers: z.array(interviewerSchema),
  interviewGuests: z.array(z.string()).optional(),
}).refine(
  (data) => {
    if (data.onlineInterview) {
      return data.interviewers.length > 0;
    }
    return true;
  },
  {
    message: 'At least one interviewer is required for online interviews',
    path: ['interviewers'],
  }
);

// Step 6: Review (no validation needed)
const step6Schema = z.object({});

// Combined schema
export const jobPostValidationSchema = z.object({
  step0: step0Schema,
  step1: step1Schema,
  step2: step2Schema,
  step3: step3Schema,
  step4: step4Schema,
  step5: step5Schema,
  step6: step6Schema,
  // Metadata (not part of API)
  currentStep: z.number().min(0).max(5), // Changed from 6 to 5 (Review is now a modal)
  mode: z.string().refine((val) => ['new', 'edit', 'clone'].includes(val), {
    message: 'Mode must be new, edit, or clone',
  }),
  jobPostId: z.string().optional(),
});

export type JobPostFormData = z.infer<typeof jobPostValidationSchema>;


